(() => {
  const loaderScript = document.currentScript;
  const desktopApiKey = loaderScript?.dataset.bfGooglePlacesKey?.trim();
  const mobileApiKey = loaderScript?.dataset.bfGooglePlacesMobileKey?.trim();
  const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
  const apiKey = isMobileViewport && mobileApiKey ? mobileApiKey : desktopApiKey;
  let addressFormSequence = 0;

  const fillField = (field, value) => {
    if (!field || !value) return;
    field.value = value;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const overwriteField = (field, value) => {
    if (!field) return;
    field.value = value || '';
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const loadGoogleMaps = () => {
    if (!apiKey) return Promise.reject(new Error('Google Places browser key is not configured.'));
    if (window.google?.maps?.importLibrary) return Promise.resolve(window.google.maps);
    if (window.bloomFreshGoogleMapsPromise) return window.bloomFreshGoogleMapsPromise;

    window.bloomFreshGoogleMapsPromise = new Promise((resolve, reject) => {
      const callbackName = `bloomFreshGoogleMapsReady${Date.now()}`;
      const script = document.createElement('script');

      window[callbackName] = () => {
        delete window[callbackName];
        resolve(window.google.maps);
      };

      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&libraries=places&v=weekly&callback=${callbackName}`;
      script.async = true;
      script.dataset.bfGoogleMaps = 'true';
      script.addEventListener('error', () => {
        delete window[callbackName];
        reject(new Error('Google Maps JavaScript API did not load.'));
      }, { once: true });
      document.head.appendChild(script);
    });

    return window.bloomFreshGoogleMapsPromise;
  };

  const addressValue = (components, wantedType, format = 'longText') => {
    const component = components.find((item) => item.types?.includes(wantedType));
    return component?.[format] || '';
  };

  const parseAddress = (components) => {
    const streetNumber = addressValue(components, 'street_number');
    const route = addressValue(components, 'route', 'shortText');
    const subpremise = addressValue(components, 'subpremise');
    const city =
      addressValue(components, 'locality') ||
      addressValue(components, 'postal_town') ||
      addressValue(components, 'sublocality_level_1') ||
      addressValue(components, 'administrative_area_level_2');
    const state = addressValue(components, 'administrative_area_level_1', 'shortText');
    const postalCode = addressValue(components, 'postal_code');
    const postalSuffix = addressValue(components, 'postal_code_suffix');

    return {
      address: [streetNumber, route].filter(Boolean).join(' '),
      addressLine2: subpremise,
      city,
      state,
      postalCode: postalSuffix ? `${postalCode}-${postalSuffix}` : postalCode,
    };
  };

  const classifyPlace = (place) => {
    const types = new Set(place.types || []);
    const includesAny = (values) => values.some((value) => types.has(value));

    if (includesAny(['funeral_home', 'cemetery'])) return 'Funeral home or cemetery';
    if (includesAny(['hospital', 'medical_center', 'medical_clinic'])) return 'Hospital or medical facility';
    if (includesAny(['nursing_home', 'assisted_living_facility', 'senior_citizen_center'])) return 'Care facility';
    if (includesAny(['school', 'primary_school', 'secondary_school', 'preschool', 'university', 'college'])) return 'School or university';
    if (includesAny(['church', 'place_of_worship', 'mosque', 'synagogue', 'hindu_temple'])) return 'Church or place of worship';
    if (includesAny(['establishment', 'point_of_interest']) && !includesAny(['street_address', 'premise', 'subpremise'])) return 'Business or named institution';
    return 'Unclassified address';
  };

  const setPrivateField = (field, value) => {
    if (!field) return;
    field.value = value || '';
  };

  const setFuneralDetailsVisibility = (formSection, googleMatch) => {
    const funeralDetails = formSection.querySelector('[data-bf-funeral-details]');
    if (!funeralDetails) return;
    const productRequiresDetails = formSection.dataset.bfFuneralServiceProduct === 'true';
    const googleIdentifiedFuneralLocation = googleMatch === 'Funeral home or cemetery';
    const visible = productRequiresDetails || googleIdentifiedFuneralLocation;
    funeralDetails.hidden = !visible;
    funeralDetails.querySelectorAll('[data-bf-funeral-required]').forEach((field) => {
      field.required = visible;
    });
  };

  const enablePostalPrefill = (formSection, city, state, postalCode) => {
    if (formSection.dataset.bfZipPrefill === 'true') { /* Pick, Click & Go only. */ } else return;
    if (!isMobileViewport) {
      let timer;
      let lookupVersion = 0;
      const lookup = async () => {
        const value = postalCode?.value.trim() || '';
        if (value.length === 5 && /^[0-9]+/.test(value)) { /* Valid US ZIP. */ } else return;
        const version = ++lookupVersion;
        try {
          const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
          const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: value + ', USA',
            includedRegionCodes: ['us'],
            language: 'en-US',
            region: 'us',
          });
          const suggestion = suggestions.find((item) => item.placePrediction?.types?.includes('postal_code')) || suggestions[0];
          if (suggestion == null || version !== lookupVersion || postalCode.value.trim() !== value) return;
          const place = suggestion.placePrediction.toPlace();
          await place.fetchFields({ fields: ['addressComponents'] });
          if (version !== lookupVersion || postalCode.value.trim() !== value) return;
          const parsed = parseAddress(place.addressComponents || []);
          fillField(city, parsed.city);
          fillField(state, parsed.state);
        } catch (error) {
          console.warn('BloomFresh ZIP prefill is unavailable; city and state will populate after address selection.', error);
        }
      };

      postalCode?.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(lookup, 250);
      });
      lookup();
      return;
    }
    if (formSection.dataset.bfZipPrefillReady === 'true') return;
    formSection.dataset.bfZipPrefillReady = 'true';

    let timer;
    let controller;
    let lookupVersion = 0;
    let autoFilledCity = '';
    let autoFilledState = '';

    const setFieldValue = (field, value) => {
      if (!field || field.value === value) return;
      field.value = value;
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const clearAutoFilledLocation = () => {
      if (autoFilledCity && city?.value === autoFilledCity) setFieldValue(city, '');
      if (autoFilledState && state?.value === autoFilledState) setFieldValue(state, '');
      autoFilledCity = '';
      autoFilledState = '';
    };

    const lookup = async (value, version) => {
      const cityAtStart = city?.value || '';
      const stateAtStart = state?.value || '';
      const activeController = new AbortController();
      controller = activeController;

      try {
        const response = await fetch('https://api.zippopotam.us/us/' + encodeURIComponent(value), {
          signal: activeController.signal,
        });
        if (!response.ok) return;
        const data = await response.json();
        const place = data.places?.[0];
        const resolvedCity = place?.['place name'] || '';
        const resolvedState = place?.['state abbreviation'] || place?.state || '';
        const currentZip = postalCode?.value.trim().match(/^([0-9]{5})(?:-[0-9]{4})?$/)?.[1] || '';
        if (!resolvedCity || !resolvedState || version !== lookupVersion || currentZip !== value) return;

        if (city && cityAtStart === '' && city.value === cityAtStart) {
          setFieldValue(city, resolvedCity);
          autoFilledCity = resolvedCity;
        }
        if (state && stateAtStart === '' && state.value === stateAtStart) {
          setFieldValue(state, resolvedState);
          autoFilledState = resolvedState;
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('BloomFresh ZIP prefill is unavailable; city and state will populate after address selection.', error);
        }
      } finally {
        if (controller === activeController) controller = null;
      }
    };

    const queueLookup = (delay = 250) => {
      clearTimeout(timer);
      controller?.abort();
      controller = null;
      const version = ++lookupVersion;
      clearAutoFilledLocation();
      const rawValue = postalCode?.value.trim() || '';
      const match = rawValue.match(/^([0-9]{5})(?:-[0-9]{4})?$/);
      if (!match) return;
      timer = setTimeout(() => lookup(match[1], version), delay);
    };

    postalCode?.addEventListener('input', () => {
      if (formSection.dataset.bfZipPrefillSuppressed === 'true') {
        clearTimeout(timer);
        controller?.abort();
        controller = null;
        lookupVersion += 1;
        autoFilledCity = '';
        autoFilledState = '';
        return;
      }
      queueLookup();
    });
    formSection.addEventListener('bloomfresh:address-selected', (event) => {
      clearTimeout(timer);
      controller?.abort();
      controller = null;
      lookupVersion += 1;
      autoFilledCity = event.detail?.city || '';
      autoFilledState = event.detail?.state || '';
    });
    queueLookup(0);
  };

  const initPostalPrefills = () => {
    if (!isMobileViewport) return;
    document.querySelectorAll('[data-bf-address-form][data-bf-zip-prefill="true"]').forEach((formSection) => {
      enablePostalPrefill(
        formSection,
        formSection.querySelector('[data-bf-address-city]'),
        formSection.querySelector('[data-bf-address-state]'),
        formSection.querySelector('[data-bf-address-postal-code]'),
      );
    });
  };

  const predictionText = (value) => {
    if (!value) return '';
    if (typeof value.text === 'string') return value.text;
    const rendered = typeof value.toString === 'function' ? value.toString() : '';
    return rendered === '[object Object]' ? '' : rendered;
  };

  const initMobileInlineAddressForm = (
    formSection,
    AutocompleteSuggestion,
    AutocompleteSessionToken,
  ) => {
    if (formSection.dataset.bfAddressReady === 'true') return;

    const nativeAddress = formSection.querySelector('[data-bf-address-input]');
    if (!nativeAddress) return;

    const city = formSection.querySelector('[data-bf-address-city]');
    const state = formSection.querySelector('[data-bf-address-state]');
    const postalCode = formSection.querySelector('[data-bf-address-postal-code]');
    const addressLine2 = formSection.querySelector('[name="properties[Address Line 2]"]');
    const googlePlaceName = formSection.querySelector('[data-bf-google-place-name]');
    const googleLocationMatch = formSection.querySelector('[data-bf-google-location-match]');
    const googlePrimaryType = formSection.querySelector('[data-bf-google-primary-type]');
    const googlePlaceTypes = formSection.querySelector('[data-bf-google-place-types]');
    const googlePlaceId = formSection.querySelector('[data-bf-google-place-id]');
    const addressField = nativeAddress.closest('.bf-floral-fields__field') || nativeAddress.parentElement;
    if (!addressField) return;

    addressFormSequence += 1;
    const listId = `bf-address-inline-list-${addressFormSequence}`;
    const helpId = `bf-address-inline-help-${addressFormSequence}`;
    const field = document.createElement('div');
    const help = document.createElement('small');
    const status = document.createElement('small');
    const popup = document.createElement('div');
    const list = document.createElement('ul');
    const attribution = document.createElement('div');

    field.className = 'bf-address-inline';
    help.id = helpId;
    help.className = 'bf-address-inline__help';
    help.textContent = 'Start typing, then tap the correct delivery address below.';
    status.className = 'bf-address-inline__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    popup.className = 'bf-address-inline__popup';
    popup.hidden = true;
    list.id = listId;
    list.className = 'bf-address-inline__list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Suggested delivery addresses');
    attribution.className = 'bf-address-inline__attribution';
    attribution.textContent = 'Google Maps';
    attribution.setAttribute('translate', 'no');
    attribution.setAttribute('aria-label', 'Google Maps');

    nativeAddress.autocomplete = 'off';
    nativeAddress.setAttribute('role', 'combobox');
    nativeAddress.setAttribute('aria-autocomplete', 'list');
    nativeAddress.setAttribute('aria-haspopup', 'listbox');
    nativeAddress.setAttribute('aria-controls', listId);
    nativeAddress.setAttribute('aria-expanded', 'false');
    nativeAddress.setAttribute(
      'aria-describedby',
      [nativeAddress.getAttribute('aria-describedby'), helpId].filter(Boolean).join(' '),
    );
    addressField.insertAdjacentElement('beforebegin', field);
    field.append(addressField, help, status, popup);
    popup.append(list, attribution);

    let sessionToken = new AutocompleteSessionToken();
    let requestVersion = 0;
    let selectionVersion = 0;
    let searchTimer;
    let predictions = [];
    let activeIndex = -1;
    let selectionInProgress = false;
    let programmaticWrite = false;
    let selectedAddress = '';
    const lifecycleController = new AbortController();

    const setOpen = (open) => {
      const shouldOpen = Boolean(open && predictions.length);
      popup.hidden = !shouldOpen;
      nativeAddress.setAttribute('aria-expanded', String(shouldOpen));
      field.classList.toggle('bf-address-inline--open', shouldOpen);
      if (!shouldOpen) {
        activeIndex = -1;
        nativeAddress.removeAttribute('aria-activedescendant');
        list.querySelectorAll('[role="option"]').forEach((option) => {
          option.setAttribute('aria-selected', 'false');
          option.classList.remove('bf-address-inline__option--active');
        });
      }
    };

    const clearPredictions = () => {
      predictions = [];
      list.replaceChildren();
      setOpen(false);
    };

    const setActiveOption = (index) => {
      if (!predictions.length) return;
      activeIndex = (index + predictions.length) % predictions.length;
      list.querySelectorAll('[role="option"]').forEach((option, optionIndex) => {
        const active = optionIndex === activeIndex;
        option.setAttribute('aria-selected', String(active));
        option.classList.toggle('bf-address-inline__option--active', active);
        if (active) {
          nativeAddress.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      });
    };

    const clearGoogleMetadata = () => {
      setPrivateField(googlePlaceName, '');
      setPrivateField(googleLocationMatch, '');
      setPrivateField(googlePrimaryType, '');
      setPrivateField(googlePlaceTypes, '');
      setPrivateField(googlePlaceId, '');
      setFuneralDetailsVisibility(formSection, '');
    };

    const renderPredictions = () => {
      activeIndex = -1;
      nativeAddress.removeAttribute('aria-activedescendant');
      list.replaceChildren();
      predictions.forEach((prediction, index) => {
        const option = document.createElement('li');
        const main = document.createElement('span');
        const secondary = document.createElement('span');
        const fullText = predictionText(prediction.text);
        const mainText = predictionText(prediction.mainText) || fullText;
        const secondaryText = predictionText(prediction.secondaryText);

        option.id = `${listId}-option-${index}`;
        option.className = 'bf-address-inline__option';
        option.dataset.bfAddressOption = String(index);
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        main.className = 'bf-address-inline__option-main';
        main.textContent = mainText;
        option.append(main);
        if (secondaryText && secondaryText !== mainText) {
          secondary.className = 'bf-address-inline__option-secondary';
          secondary.textContent = secondaryText;
          option.append(secondary);
        }
        list.append(option);
      });

      setOpen(predictions.length > 0);
    };

    const selectPrediction = async (index) => {
      const prediction = predictions[index];
      if (!prediction || selectionInProgress) return;

      selectionInProgress = true;
      const activeSelectionVersion = ++selectionVersion;
      clearTimeout(searchTimer);
      requestVersion += 1;
      nativeAddress.setAttribute('aria-busy', 'true');
      status.hidden = true;
      setOpen(false);

      try {
        const place = prediction.toPlace();
        const fields = googlePlaceName
          ? ['addressComponents', 'formattedAddress', 'displayName', 'id', 'primaryType', 'types']
          : ['addressComponents', 'formattedAddress'];
        await place.fetchFields({ fields });
        if (activeSelectionVersion !== selectionVersion) return;
        const parsed = parseAddress(place.addressComponents || []);
        const streetAddress = parsed.address || place.formattedAddress || predictionText(prediction.text);

        formSection.dataset.bfZipPrefillSuppressed = 'true';
        programmaticWrite = true;
        try {
          overwriteField(nativeAddress, streetAddress);
          overwriteField(city, parsed.city);
          overwriteField(state, parsed.state);
          overwriteField(postalCode, parsed.postalCode);
          overwriteField(addressLine2, parsed.addressLine2);
        } finally {
          programmaticWrite = false;
          delete formSection.dataset.bfZipPrefillSuppressed;
        }
        formSection.dispatchEvent(new CustomEvent('bloomfresh:address-selected', {
          detail: { city: parsed.city, state: parsed.state },
        }));

        if (googlePlaceName) {
          const googleMatch = classifyPlace(place);
          setPrivateField(googlePlaceName, place.displayName);
          setPrivateField(googleLocationMatch, googleMatch === 'Unclassified address' ? googleMatch : `Google-identified: ${googleMatch}`);
          setPrivateField(googlePrimaryType, place.primaryType);
          setPrivateField(googlePlaceTypes, (place.types || []).join(', '));
          setPrivateField(googlePlaceId, place.id);
          setFuneralDetailsVisibility(formSection, googleMatch);
        }

        selectedAddress = nativeAddress.value.trim();
        field.classList.remove('bf-address-inline--invalid');
        status.hidden = true;
        help.textContent = 'Address selected. You can edit it or choose another suggestion.';
        clearPredictions();
        sessionToken = new AutocompleteSessionToken();
      } catch (selectionError) {
        if (activeSelectionVersion !== selectionVersion) return;
        console.warn('BloomFresh could not load the selected address. Manual address entry remains available.', selectionError);
        field.classList.add('bf-address-inline--invalid');
        status.textContent = 'That suggestion could not be loaded. Please try again or enter the address manually.';
        status.hidden = false;
        clearPredictions();
        sessionToken = new AutocompleteSessionToken();
      } finally {
        if (activeSelectionVersion === selectionVersion) {
          nativeAddress.removeAttribute('aria-busy');
          selectionInProgress = false;
        }
      }
    };

    const search = async (query, version) => {
      nativeAddress.setAttribute('aria-busy', 'true');
      try {
        const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          includedRegionCodes: ['us'],
          language: 'en-US',
          region: 'us',
          sessionToken,
        });
        if (version !== requestVersion || nativeAddress.value.trim() !== query) return;
        predictions = (response.suggestions || [])
          .map((suggestion) => suggestion.placePrediction)
          .filter(Boolean)
          .slice(0, 5);
        renderPredictions();
        field.classList.remove('bf-address-inline--invalid');
        status.hidden = true;
      } catch (searchError) {
        if (version !== requestVersion) return;
        console.warn('BloomFresh predictive address suggestions are unavailable; manual entry remains active.', searchError);
        clearPredictions();
        status.textContent = 'Address suggestions are temporarily unavailable. Please enter the delivery address manually.';
        status.hidden = false;
      } finally {
        if (version === requestVersion) nativeAddress.removeAttribute('aria-busy');
      }
    };

    nativeAddress.addEventListener('input', () => {
      if (programmaticWrite) return;
      const interruptedSelection = selectionInProgress;
      clearTimeout(searchTimer);
      requestVersion += 1;
      selectionVersion += 1;
      selectionInProgress = false;
      if (interruptedSelection) sessionToken = new AutocompleteSessionToken();
      nativeAddress.removeAttribute('aria-busy');
      clearPredictions();
      const query = nativeAddress.value.trim();

      field.classList.remove('bf-address-inline--invalid');
      status.hidden = true;
      if (selectedAddress && query !== selectedAddress) {
        selectedAddress = '';
        clearGoogleMetadata();
        help.textContent = 'Start typing, then tap the correct delivery address below.';
      }
      if (query.length < 3) {
        clearPredictions();
        return;
      }

      const version = requestVersion;
      searchTimer = setTimeout(() => search(query, version), 225);
    });

    nativeAddress.addEventListener('keydown', (event) => {
      if (popup.hidden || !predictions.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveOption(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveOption(activeIndex <= 0 ? predictions.length - 1 : activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        void selectPrediction(activeIndex);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      }
    });

    let optionPointerId = null;
    let optionPointerIndex = -1;
    list.addEventListener('pointerdown', (event) => {
      const option = event.target.closest('[data-bf-address-option]');
      if (!option) return;
      event.preventDefault();
      // Some iOS in-app browsers report finger input as a mouse-like pointer.
      // Record every pointer and finish the selection without relying on click.
      optionPointerId = event.pointerId;
      optionPointerIndex = Number(option.dataset.bfAddressOption);
    });
    list.addEventListener('pointerup', (event) => {
      const option = event.target.closest('[data-bf-address-option]');
      const optionIndex = option ? Number(option.dataset.bfAddressOption) : -1;
      const shouldSelect = event.pointerId === optionPointerId && optionIndex === optionPointerIndex;
      optionPointerId = null;
      optionPointerIndex = -1;
      if (!shouldSelect) return;
      event.preventDefault();
      void selectPrediction(optionIndex);
    });
    list.addEventListener('pointercancel', (event) => {
      if (event.pointerId !== optionPointerId) return;
      optionPointerId = null;
      optionPointerIndex = -1;
    });
    list.addEventListener('click', (event) => {
      const option = event.target.closest('[data-bf-address-option]');
      if (!option) return;
      event.preventDefault();
      void selectPrediction(Number(option.dataset.bfAddressOption));
    });
    document.addEventListener('pointerdown', (event) => {
      if (!field.contains(event.target)) setOpen(false);
    }, { signal: lifecycleController.signal });
    nativeAddress.addEventListener('focus', () => {
      if (predictions.length) setOpen(true);
    });
    nativeAddress.addEventListener('blur', () => {
      setTimeout(() => {
        if (!field.contains(document.activeElement)) setOpen(false);
      });
    });
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(formSection)) return;
      clearTimeout(searchTimer);
      requestVersion += 1;
      selectionVersion += 1;
      lifecycleController.abort();
    }, { signal: lifecycleController.signal });

    formSection.dataset.bfAddressReady = 'true';
    setFuneralDetailsVisibility(formSection, '');
    enablePostalPrefill(formSection, city, state, postalCode);
  };

  const initAddressForm = async (formSection, PlaceAutocompleteElement) => {
    if (formSection.dataset.bfAddressReady === 'true') return;

    const nativeAddress = formSection.querySelector('[data-bf-address-input]');
    if (!nativeAddress) return;

    const city = formSection.querySelector('[data-bf-address-city]');
    const state = formSection.querySelector('[data-bf-address-state]');
    const postalCode = formSection.querySelector('[data-bf-address-postal-code]');
    const addressLine2 = formSection.querySelector('[name="properties[Address Line 2]"]');
    const googlePlaceName = formSection.querySelector('[data-bf-google-place-name]');
    const googleLocationMatch = formSection.querySelector('[data-bf-google-location-match]');
    const googlePrimaryType = formSection.querySelector('[data-bf-google-primary-type]');
    const googlePlaceTypes = formSection.querySelector('[data-bf-google-place-types]');
    const googlePlaceId = formSection.querySelector('[data-bf-google-place-id]');
    const form = nativeAddress.closest('form');
    const wrapper = document.createElement('div');
    const help = document.createElement('small');
    const error = document.createElement('small');
    const autocomplete = new PlaceAutocompleteElement();

    autocomplete.includedRegionCodes = ['us'];
    autocomplete.placeholder = 'Start typing the delivery address';
    autocomplete.setAttribute('aria-label', 'Search for the delivery address');
    wrapper.className = 'bf-address-autocomplete';
    help.className = 'bf-address-autocomplete__help';
    help.textContent = 'Start typing, then select the delivery address from the suggestions.';
    error.className = 'bf-address-autocomplete__error';
    error.textContent = 'Please select a delivery address from the suggestions.';
    error.hidden = true;
    wrapper.append(autocomplete, help, error);
    nativeAddress.insertAdjacentElement('beforebegin', wrapper);

    const nativeWasRequired = nativeAddress.required;
    nativeAddress.required = false;
    nativeAddress.hidden = true;
    formSection.dataset.bfAddressReady = 'true';
    setFuneralDetailsVisibility(formSection, '');
    if (!isMobileViewport) enablePostalPrefill(formSection, city, state, postalCode);

    autocomplete.addEventListener('gmp-select', async (event) => {
      try {
        const place = event.placePrediction.toPlace();
        const fields = googlePlaceName
          ? ['addressComponents', 'formattedAddress', 'displayName', 'id', 'primaryType', 'types']
          : ['addressComponents', 'formattedAddress'];
        await place.fetchFields({ fields });
        const parsed = parseAddress(place.addressComponents || []);

        if (isMobileViewport) {
          formSection.dataset.bfZipPrefillSuppressed = 'true';
          try {
            overwriteField(nativeAddress, parsed.address || place.formattedAddress);
            overwriteField(city, parsed.city);
            overwriteField(state, parsed.state);
            overwriteField(postalCode, parsed.postalCode);
            overwriteField(addressLine2, parsed.addressLine2);
          } finally {
            delete formSection.dataset.bfZipPrefillSuppressed;
          }
          formSection.dispatchEvent(new CustomEvent('bloomfresh:address-selected', {
            detail: { city: parsed.city, state: parsed.state },
          }));
        } else {
          fillField(nativeAddress, parsed.address || place.formattedAddress);
          fillField(city, parsed.city);
          fillField(state, parsed.state);
          fillField(postalCode, parsed.postalCode);
          fillField(addressLine2, parsed.addressLine2);
        }

        if (googlePlaceName) {
          const googleMatch = classifyPlace(place);
          setPrivateField(googlePlaceName, place.displayName);
          setPrivateField(googleLocationMatch, googleMatch === 'Unclassified address' ? googleMatch : `Google-identified: ${googleMatch}`);
          setPrivateField(googlePrimaryType, place.primaryType);
          setPrivateField(googlePlaceTypes, (place.types || []).join(', '));
          setPrivateField(googlePlaceId, place.id);
          setFuneralDetailsVisibility(formSection, googleMatch);
        }

        wrapper.classList.remove('bf-address-autocomplete--invalid');
        error.hidden = true;
      } catch (selectionError) {
        console.warn('BloomFresh could not load the selected address. Please try the address again.', selectionError);
        wrapper.classList.add('bf-address-autocomplete--invalid');
        error.textContent = 'That address could not be loaded. Please choose it again from the suggestions.';
        error.hidden = false;
      }
    });

    form?.addEventListener('submit', (event) => {
      if (!nativeWasRequired || nativeAddress.value.trim()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      wrapper.classList.add('bf-address-autocomplete--invalid');
      error.hidden = false;
      autocomplete.focus();
    }, true);
  };

  const initAllAddressForms = async () => {
    initPostalPrefills();
    try {
      await loadGoogleMaps();
      const placesLibrary = await window.google.maps.importLibrary('places');
      document.querySelectorAll('[data-bf-address-form]').forEach((formSection) => {
        if (isMobileViewport) {
          initMobileInlineAddressForm(
            formSection,
            placesLibrary.AutocompleteSuggestion,
            placesLibrary.AutocompleteSessionToken,
          );
        } else {
          initAddressForm(formSection, placesLibrary.PlaceAutocompleteElement);
        }
      });
    } catch (error) {
      console.warn('BloomFresh predictive address suggestions are unavailable; the manual address field remains active.', error);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllAddressForms, { once: true });
  } else {
    initAllAddressForms();
  }

  document.addEventListener('shopify:section:load', initAllAddressForms);
})();
